import React, { useEffect, useState } from "react";
import { db, firestore } from "./firebaseConfig";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import AdminNavbar from "../layouts/AdminNavbar";
import Footer from "../layouts/Footer";

const BulkMigrate = () => {
  const access = sessionStorage.getItem("mandftoken");

  if (!access) {
    window.location.href = "/";
    toast.info("You are not allowed to view this page.");
  }

  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState("");
  const [migrationResults, setMigrationResults] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "products"));
      const productsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsList);
    } catch (error) {
      toast.error("Error fetching products");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadUrlToCloudinary = async (imageUrl, productId, index) => {
    try {
      const response = await fetch("/api/upload-from-url", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imageUrl: imageUrl,
          folder: `products/${productId}`,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Upload failed");
      }

      return data.cloudinaryUrl;
    } catch (error) {
      console.error(`Error uploading image ${index + 1}:`, error);
      throw error;
    }
  };

  const migrateProduct = async (product) => {
    try {
      const { id, imageUrls, title } = product;

      if (!imageUrls || imageUrls.length === 0) {
        return { success: true, skipped: true, reason: "No images" };
      }

      // Check if already on Cloudinary
      const isCloudinary = imageUrls[0]?.includes("cloudinary.com");
      if (isCloudinary) {
        return { success: true, skipped: true, reason: "Already on Cloudinary" };
      }

      setProgress(`Migrating "${title}" (${imageUrls.length} images)...`);

      // Upload each image to Cloudinary
      const cloudinaryUrls = [];
      for (let i = 0; i < imageUrls.length; i++) {
        setProgress(
          `Uploading image ${i + 1}/${imageUrls.length} for "${title}"`
        );
        const cloudinaryUrl = await uploadUrlToCloudinary(imageUrls[i], id, i);
        cloudinaryUrls.push(cloudinaryUrl);

        // Small delay to avoid rate limits
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Update Firestore with new Cloudinary URLs
      const docRef = doc(firestore, "products", id);
      await updateDoc(docRef, {
        imageUrls: cloudinaryUrls,
        oldFirebaseUrls: imageUrls,
        migratedAt: new Date().toISOString(),
      });

      return {
        success: true,
        productId: id,
        productTitle: title,
        imageCount: cloudinaryUrls.length,
      };
    } catch (error) {
      return {
        success: false,
        productId: product.id,
        productTitle: product.title,
        error: error.message,
      };
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select only products with Firebase Storage URLs (not Cloudinary)
      const firebaseProducts = filteredProducts
        .filter(
          (p) =>
            p.imageUrls &&
            p.imageUrls.length > 0 &&
            !p.imageUrls[0]?.includes("cloudinary.com")
        )
        .map((p) => p.id);
      setSelectedProducts(firebaseProducts);
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter((id) => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleBulkMigrate = async () => {
    if (selectedProducts.length === 0) {
      toast.warning("Please select at least one product to migrate");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to migrate ${selectedProducts.length} product(s) to Cloudinary?`
      )
    ) {
      return;
    }

    setMigrating(true);
    setProgress("Starting migration...");

    const results = {
      total: selectedProducts.length,
      migrated: 0,
      skipped: 0,
      failed: 0,
      details: [],
    };

    try {
      for (let i = 0; i < selectedProducts.length; i++) {
        const product = products.find((p) => p.id === selectedProducts[i]);
        if (!product) continue;

        setProgress(`Processing ${i + 1}/${selectedProducts.length}...`);

        const result = await migrateProduct(product);

        if (result.success && !result.skipped) {
          results.migrated++;
          results.details.push({
            type: "success",
            title: result.productTitle,
            message: `Migrated ${result.imageCount} image(s)`,
          });
        } else if (result.skipped) {
          results.skipped++;
          results.details.push({
            type: "skipped",
            title: product.title,
            message: result.reason,
          });
        } else {
          results.failed++;
          results.details.push({
            type: "error",
            title: result.productTitle,
            message: result.error,
          });
        }

        // Delay between products
        if (i < selectedProducts.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      setMigrationResults(results);
      toast.success(
        `Migration complete! Migrated: ${results.migrated}, Skipped: ${results.skipped}, Failed: ${results.failed}`
      );

      // Refresh products list
      await fetchProducts();
      setSelectedProducts([]);
    } catch (error) {
      toast.error("Migration error: " + error.message);
      console.error(error);
    } finally {
      setMigrating(false);
      setProgress("");
    }
  };

  const filteredProducts = products.filter((product) => {
    if (query === "") return true;
    const searchLower = query.toLowerCase();
    return (
      product.title?.toLowerCase().includes(searchLower) ||
      product.category?.toLowerCase().includes(searchLower)
    );
  });

  const isOnCloudinary = (product) => {
    return (
      product.imageUrls &&
      product.imageUrls.length > 0 &&
      product.imageUrls[0]?.includes("cloudinary.com")
    );
  };

  return (
    <>
      <AdminNavbar />
      <section className="ftco-section">
        <div className="container">
          <div className="row mb-4">
            <div className="col-12">
              <h2 className="mb-3">Bulk Migrate Images to Cloudinary</h2>
              <p className="text-muted">
                Select products to migrate from Firebase Storage to Cloudinary
              </p>
            </div>
          </div>

          {migrationResults && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">Migration Results</h5>
                  </div>
                  <div className="card-body">
                    <div className="row text-center mb-3">
                      <div className="col-md-4">
                        <div className="card bg-success text-white">
                          <div className="card-body">
                            <h3>{migrationResults.migrated}</h3>
                            <p className="mb-0">Migrated</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-secondary text-white">
                          <div className="card-body">
                            <h3>{migrationResults.skipped}</h3>
                            <p className="mb-0">Skipped</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-danger text-white">
                          <div className="card-body">
                            <h3>{migrationResults.failed}</h3>
                            <p className="mb-0">Failed</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <h6>Details:</h6>
                      <div className="list-group" style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {migrationResults.details.map((detail, i) => (
                          <div
                            key={i}
                            className={`list-group-item list-group-item-${
                              detail.type === "success"
                                ? "success"
                                : detail.type === "skipped"
                                ? "secondary"
                                : "danger"
                            }`}
                          >
                            <strong>{detail.title}</strong>: {detail.message}
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      className="btn btn-primary mt-3"
                      onClick={() => setMigrationResults(null)}
                    >
                      Close Results
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {migrating && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5>Migration in Progress...</h5>
                    <p className="text-muted">{progress}</p>
                    <div className="alert alert-warning">
                      Please don't close this page or refresh the browser
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!migrating && (
            <>
              <div className="row mb-3">
                <div className="col-md-8">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search products..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleBulkMigrate}
                    disabled={selectedProducts.length === 0 || migrating}
                  >
                    Migrate Selected ({selectedProducts.length})
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: "50px" }}>
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              selectedProducts.length > 0 &&
                              selectedProducts.length ===
                                filteredProducts.filter(
                                  (p) =>
                                    p.imageUrls &&
                                    p.imageUrls.length > 0 &&
                                    !isOnCloudinary(p)
                                ).length
                            }
                          />
                        </th>
                        <th>Title</th>
                        <th>Category</th>
                        <th style={{ width: "100px" }}>Images</th>
                        <th style={{ width: "150px" }}>Storage</th>
                        <th style={{ width: "120px" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => {
                        const onCloudinary = isOnCloudinary(product);
                        const hasImages =
                          product.imageUrls && product.imageUrls.length > 0;

                        return (
                          <tr key={product.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedProducts.includes(product.id)}
                                onChange={() => handleSelectProduct(product.id)}
                                disabled={!hasImages || onCloudinary}
                              />
                            </td>
                            <td>
                              <strong>{product.title}</strong>
                            </td>
                            <td>
                              <span className="badge bg-secondary text-capitalize">
                                {product.category}
                              </span>
                            </td>
                            <td className="text-center">
                              {product.imageUrls?.length || 0}
                            </td>
                            <td>
                              {!hasImages ? (
                                <span className="badge bg-warning">No Images</span>
                              ) : onCloudinary ? (
                                <span className="badge bg-success">
                                  ✓ Cloudinary
                                </span>
                              ) : (
                                <span className="badge bg-info">
                                  Firebase Storage
                                </span>
                              )}
                            </td>
                            <td>
                              {!hasImages ? (
                                <span className="text-muted">-</span>
                              ) : onCloudinary ? (
                                <span className="text-success">✓ Migrated</span>
                              ) : (
                                <span className="text-warning">
                                  ⚠ Needs Migration
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {filteredProducts.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-muted">No products found</p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3">
                <div className="alert alert-info">
                  <h6>Legend:</h6>
                  <ul className="mb-0">
                    <li>
                      <span className="badge bg-success">✓ Cloudinary</span> -
                      Already migrated to Cloudinary
                    </li>
                    <li>
                      <span className="badge bg-info">Firebase Storage</span> -
                      Ready to migrate
                    </li>
                    <li>
                      <span className="badge bg-warning">No Images</span> - Product
                      has no images
                    </li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default BulkMigrate;